/**
 * NOS-53: Public blog listing at /blog or /:orgSlug/blog.
 * Fetches published posts, category filter, pagination, SEO.
 * NOS-265: org/nav/customizations shell via usePublicSiteShell + PublicSitePage.
 */

import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { supabase } from "@/lib/supabase"
import { PublicSitePage } from "@/components/PublicSitePage"
import { ListingPagination, useScrollToListingTop } from "@/components/public/ListingPagination"
import { SEOHead } from "@/components/public/SEOHead"
import { getPublicSiteOrigin } from "@/lib/publicSiteUrl"
import { usePublicSiteShell } from "@/hooks/usePublicSiteShell"
import type { Organization, OrgCustomizations } from "@/templates/types"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { FileText } from "lucide-react"

const POSTS_PER_PAGE = 20

interface BlogPostRow {
  id: string
  org_id: string
  title: string
  slug: string
  excerpt: string | null
  cover_image: string | null
  category_id: string | null
  author_id: string | null
  published_at: string | null
  blog_categories?: { name: string; slug: string; color: string | null } | null
}

interface BlogListingPageProps {
  orgSlug: string
  org?: Organization | null
  customizations?: OrgCustomizations | null
  /** When on subdomain, parent provides org/cust; else we fetch by orgSlug */
  basePath?: string
}

function truncateExcerpt(text: string | null, maxLen: number): string {
  if (!text) return ""
  if (text.length <= maxLen) return text
  return text.slice(0, maxLen).trim() + "…"
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return ""
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  })
}

export function BlogListingPage({
  orgSlug,
  org: orgProp,
  customizations: custProp,
  basePath = "",
}: BlogListingPageProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const categorySlug = searchParams.get("category")
  const pageParam = parseInt(searchParams.get("page") ?? "1", 10)
  const page = Math.max(1, isNaN(pageParam) ? 1 : pageParam)

  const shell = usePublicSiteShell(orgSlug, {
    org: orgProp,
    customizations: custProp,
    basePath,
  })
  const { org, orgName, primaryColor } = shell

  const [posts, setPosts] = useState<BlogPostRow[]>([])
  const [categories, setCategories] = useState<Array<{ id: string; name: string; slug: string; color: string | null }>>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)

  const pathForPost = (slug: string) => `${basePath}/blog/${slug}`
  const pathForCategory = (slug: string) => `${basePath}/blog?category=${slug}`

  /** NOS-240: scroll the listing (not the whole window) back to its top on page change. */
  const listingTopRef = useRef<HTMLElement>(null)
  useScrollToListingTop(page, listingTopRef)

  const orgId = org?.id
  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!orgId) return

      const { data: catData } = await supabase
        .from("blog_categories")
        .select("id, name, slug, color")
        .eq("org_id", orgId)
        .order("name")
      if (!cancelled && catData) setCategories(catData as typeof categories)

      let postsQuery = supabase
        .from("blog_posts")
        .select("id, org_id, title, slug, excerpt, cover_image, category_id, author_id, published_at", { count: "exact" })
        .eq("org_id", orgId)
        .eq("is_published", true)
        .not("published_at", "is", null)
        .lte("published_at", new Date().toISOString())
        .order("published_at", { ascending: false })
        .range((page - 1) * POSTS_PER_PAGE, page * POSTS_PER_PAGE - 1)

      if (categorySlug && catData) {
        const cat = (catData as { slug: string; id: string }[]).find((c) => c.slug === categorySlug)
        if (cat) postsQuery = postsQuery.eq("category_id", cat.id)
      }

      const { data: postsData, count, error: postsErr } = await postsQuery
      if (cancelled) return
      if (postsErr) {
        setLoading(false)
        return
      }
      setPosts((postsData ?? []) as BlogPostRow[])
      setTotalCount(count ?? 0)
      setLoading(false)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [orgId, categorySlug, page])

  const canonicalUrl = useMemo(() => {
    if (!org) return ""
    const params = new URLSearchParams()
    if (categorySlug) params.set("category", categorySlug)
    if (page > 1) params.set("page", String(page))
    const q = params.toString()
    /** Public site lives on org subdomain / custom domain — not app.getnos.ai /:orgSlug/... */
    const blogPath = "/blog"
    return `${getPublicSiteOrigin(org)}${blogPath}${q ? `?${q}` : ""}`
  }, [org, categorySlug, page])

  const blogJsonLd = useMemo(() => {
    if (!org) return null
    const origin = getPublicSiteOrigin(org)
    return {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: `${orgName} Blog`,
      blogPost: posts.map((p) => ({
        "@type": "BlogPosting",
        headline: p.title,
        datePublished: p.published_at,
        url: `${origin}/blog/${p.slug}`,
      })),
    }
  }, [org, orgName, posts])

  const totalPages = Math.ceil(totalCount / POSTS_PER_PAGE)

  const description = `Stories and updates from ${orgName}`

  return (
    <PublicSitePage
      shell={shell}
      head={
        org && canonicalUrl ? (
          <SEOHead
            title={`${orgName} Blog`}
            description={description}
            canonicalUrl={canonicalUrl}
            ogType="website"
            imageUrl={org.logo_url}
            jsonLd={blogJsonLd}
          />
        ) : null
      }
    >
      <main ref={listingTopRef} className="mx-auto max-w-6xl scroll-mt-20 px-4 py-12 sm:px-6 lg:px-8">
        <header className="mb-10">
          <h1 className="text-3xl font-bold" style={{ color: primaryColor }}>
            Blog
          </h1>
          <p className="mt-1 text-muted-foreground">
            Stories and updates from {orgName}
          </p>
        </header>

        {categories.length > 0 && (
          <div className="mb-8 flex flex-wrap gap-2">
            <Link to={`${basePath}/blog`}>
              <Badge
                variant={!categorySlug ? "default" : "outline"}
                className="cursor-pointer"
              >
                All
              </Badge>
            </Link>
            {categories.map((c) => (
              <Link key={c.id} to={pathForCategory(c.slug)}>
                <Badge
                  variant={categorySlug === c.slug ? "default" : "outline"}
                  className="cursor-pointer"
                  style={categorySlug === c.slug && c.color ? { backgroundColor: c.color } : undefined}
                >
                  {c.name}
                </Badge>
              </Link>
            ))}
          </div>
        )}

        {loading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-0">
                  <div className="aspect-video bg-muted" />
                  <div className="space-y-2 p-4">
                    <div className="h-4 w-3/4 rounded bg-muted" />
                    <div className="h-3 w-full rounded bg-muted" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
            <FileText className="h-12 w-12 text-muted-foreground" />
            <p className="mt-4 text-lg font-medium">No blog posts yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Check back later for updates.
            </p>
          </div>
        ) : (
          <>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {posts.map((post) => {
                const cat = post.category_id
                  ? categories.find((c) => c.id === post.category_id)
                  : null
                return (
                  <Link key={post.id} to={pathForPost(post.slug)} className="group">
                    <Card className="h-full overflow-hidden transition hover:shadow-lg">
                      <div className="aspect-video overflow-hidden bg-muted">
                        {post.cover_image ? (
                          <img
                            src={post.cover_image}
                            alt=""
                            className="h-full w-full object-cover transition group-hover:scale-105"
                          />
                        ) : (
                          <div
                            className="flex h-full items-center justify-center"
                            style={{ backgroundColor: `${primaryColor}20` }}
                          >
                            <FileText className="h-12 w-12 opacity-50" style={{ color: primaryColor }} />
                          </div>
                        )}
                      </div>
                      <CardContent className="p-4">
                        {cat && (
                          <Badge
                            variant="secondary"
                            className="mb-2 text-xs"
                            style={cat.color ? { backgroundColor: `${cat.color}30`, color: cat.color } : undefined}
                          >
                            {cat.name}
                          </Badge>
                        )}
                        <h2 className="font-semibold group-hover:underline">
                          {post.title}
                        </h2>
                        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                          {truncateExcerpt(post.excerpt, 120)}
                        </p>
                        <p className="mt-2 text-xs text-muted-foreground">
                          {formatDate(post.published_at)}
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                )
              })}
            </div>

            <ListingPagination
              page={page}
              totalPages={totalPages}
              onPageChange={(next) =>
                setSearchParams((p) => {
                  if (next > 1) p.set("page", String(next))
                  else p.delete("page")
                  return p
                })
              }
            />
          </>
        )}
      </main>
    </PublicSitePage>
  )
}
